import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaCheckCircle, FaTimesCircle, FaShieldAlt, FaFileAlt,
  FaIdCard, FaUser, FaCalendarAlt, FaPhone, FaEnvelope, FaExpand,
  FaSpinner, FaExclamationCircle, FaTimes,
} from 'react-icons/fa';
import { getKYCById, approveKYC, rejectKYC } from '../../../api/services/adminService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import './KYCManagement.css';

/* ─── helpers ─── */
const ID_TYPE_MAP = { NATIONAL_ID: 'National ID', PASSPORT: 'Passport', DRIVING_LICENSE: 'Driving License' };
const STATUS_MAP  = { PENDING: 'pending', VERIFIED: 'verified', APPROVED: 'verified', REJECTED: 'rejected', REJECTED_RESUBMIT_ALLOWED: 'rejected' };
const formatDate  = (ds) => ds ? new Date(ds).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const isPDF       = (url) => { const l = (url || '').toLowerCase(); return l.endsWith('.pdf') || l.includes('.pdf?'); };

/* ─── Lightbox ─── */
const Lightbox = ({ blobUrl, label, isPdf, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="kycd-lightbox-overlay" onClick={onClose}>
      <div className="kycd-lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="kycd-lightbox-header">
          <span className="kycd-lightbox-label"><FaFileAlt /> {label}</span>
          <button type="button" className="kycd-lightbox-close" onClick={onClose} title="Close">
            <FaTimes />
          </button>
        </div>
        <div className="kycd-lightbox-body">
          {isPdf ? (
            <iframe src={blobUrl} title={label} className="kycd-lightbox-iframe" />
          ) : (
            <img src={blobUrl} alt={label} className="kycd-lightbox-img" />
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── InfoRow ─── */
const InfoRow = ({ icon, label, value }) => (
  <div className="kycd-info-row">
    <div className="kycd-info-icon">{icon}</div>
    <div>
      <div className="kycd-info-label">{label}</div>
      <div className="kycd-info-value">{value || '—'}</div>
    </div>
  </div>
);

/* ─── StatusBadge ─── */
const StatusBadge = ({ status }) => {
  const cfg = {
    pending:  { label: 'Pending Review', cls: 'kyc-badge--pending'  },
    verified: { label: 'Approved',       cls: 'kyc-badge--approved' },
    rejected: { label: 'Rejected',       cls: 'kyc-badge--rejected' },
  }[status] || { label: status, cls: '' };
  return <span className={`kyc-badge kyc-badge--lg ${cfg.cls}`}>{cfg.label}</span>;
};

/* ─── DocCard ─── */
const DocCard = ({ label, originalUrl }) => {
  const [blobUrl, setBlobUrl]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!originalUrl || !originalUrl.startsWith('http')) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchAuthenticatedImage(originalUrl)
      .then((url) => { if (!cancelled) { setBlobUrl(url); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [originalUrl]);

  if (!originalUrl) return null;
  const isDoc = isPDF(originalUrl);

  return (
    <>
      <div className="kycd-doc-card">
        <div className="kycd-doc-label">
          <FaFileAlt />
          <span>{label}</span>
          {blobUrl && (
            <button type="button" className="kycd-doc-open" onClick={() => setLightbox(true)} title="View full size">
              <FaExpand />
            </button>
          )}
        </div>
        <div
          className="kycd-doc-preview"
          onClick={() => blobUrl && setLightbox(true)}
          style={{ cursor: blobUrl ? 'zoom-in' : 'default' }}
        >
          {loading && (
            <div className="kycd-doc-loading"><FaSpinner className="kycd-spin" /><span>Loading…</span></div>
          )}
          {error && (
            <div className="kycd-doc-error"><FaExclamationCircle /><span>Failed to load</span></div>
          )}
          {!loading && !error && blobUrl && (
            isDoc ? (
              <iframe src={blobUrl} title={label} className="kycd-doc-iframe" />
            ) : (
              <img src={blobUrl} alt={label} className="kycd-doc-img" />
            )
          )}
          {!loading && !error && !blobUrl && originalUrl && (
            <div className="kycd-doc-loading"><FaFileAlt style={{ fontSize: 28, opacity: 0.3 }} /></div>
          )}
        </div>
        {blobUrl && (
          <div className="kycd-doc-footer">
            <button type="button" className="kycd-doc-expand-btn" onClick={() => setLightbox(true)}>
              <FaExpand /> View Full Size
            </button>
          </div>
        )}
      </div>
      {lightbox && blobUrl && (
        <Lightbox blobUrl={blobUrl} label={label} isPdf={isDoc} onClose={() => setLightbox(false)} />
      )}
    </>
  );
};

/* ─── Reject Modal ─── */
const REASON_CODES = [
  { value: 'BLURRY_IMAGE',          label: 'Blurry / unreadable image' },
  { value: 'EXPIRED_DOCUMENT',      label: 'Document expired' },
  { value: 'MISMATCH',              label: 'Name / details mismatch' },
  { value: 'INCOMPLETE_SUBMISSION', label: 'Incomplete submission' },
  { value: 'FRAUDULENT_DOCUMENT',   label: 'Suspected fraudulent document' },
  { value: 'LOW_QUALITY_SELFIE',    label: 'Low quality selfie' },
  { value: 'OTHER',                 label: 'Other (specify below)' },
];

const RejectModal = ({ onClose, onSubmit, busy }) => {
  const [reason, setReason] = useState('');
  const [code, setCode]     = useState('');
  const [err, setErr]       = useState('');

  const submit = () => {
    if (!reason.trim()) { setErr('Please enter a rejection reason.'); return; }
    if (!code)          { setErr('Please select a reason code.'); return; }
    onSubmit(reason.trim(), code);
  };

  return (
    <div className="kycd-overlay" onClick={onClose}>
      <div className="kycd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kycd-modal-header">
          <h2><FaTimesCircle className="kycd-modal-icon kycd-modal-icon--reject" /> Reject KYC Submission</h2>
          <button type="button" className="kycd-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="kycd-modal-body">
          <label className="kycd-field-label">Reason Code <span className="kycd-req">*</span></label>
          <select className="kycd-select" value={code} onChange={(e) => setCode(e.target.value)} disabled={busy}>
            <option value="">Select a code…</option>
            {REASON_CODES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <label className="kycd-field-label" style={{ marginTop: 16 }}>
            Rejection Reason <span className="kycd-req">*</span>
          </label>
          <textarea
            className="kycd-textarea" rows={4} disabled={busy}
            placeholder="Describe why this submission is being rejected. This message will be shown to the user."
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
          {err && <div className="kycd-field-err">{err}</div>}
        </div>
        <div className="kycd-modal-footer">
          <button type="button" className="kycd-btn kycd-btn--outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="kycd-btn kycd-btn--reject" onClick={submit} disabled={busy}>
            {busy ? <><FaSpinner className="kycd-spin" /> Rejecting…</> : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Toast ─── */
const Toast = ({ type, message, onClose }) => (
  <div className={`kycd-toast kycd-toast--${type}`}>
    {type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
    <span>{message}</span>
    <button type="button" onClick={onClose}>×</button>
  </div>
);

/* ─── Main Component ─── */
const KYCDetails = () => {
  const { kycId }  = useParams();
  const navigate   = useNavigate();
  const [kyc, setKyc]             = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [busy, setBusy]           = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [toast, setToast]         = useState(null);

  const showToast = (type, msg) => { setToast({ type, message: msg }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await getKYCById(kycId);
      const item = res?.data || res;
      setKyc({
        id:              item.id,
        name:            item.userName || item.userEmail || 'Unknown',
        email:           item.userEmail || item.email || '—',
        phone:           item.phone || '—',
        role:            item.role || item.userRole || '—',
        idType:          ID_TYPE_MAP[item.idType] || item.idType || '—',
        idNumber:        item.idNumber || '—',
        status:          STATUS_MAP[item.status] || (item.status || '').toLowerCase(),
        submittedAt:     formatDate(item.createdAt),
        verifiedAt:      item.verifiedAt ? formatDate(item.verifiedAt) : null,
        rejectionReason: item.rejectionReason || null,
        rejectionCode:   item.rejectionReasonCode || null,
        resubmitDeadline:item.resubmitDeadline ? formatDate(item.resubmitDeadline) : null,
        idFrontUrl:      item.idDocumentUrl || null,
        idBackUrl:       item.idDocumentBackUrl || null,
        selfieUrl:       item.selfieUrl || null,
      });
    } catch (e) { setError(e.message || 'Failed to load KYC submission'); }
    finally     { setLoading(false); }
  }, [kycId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!window.confirm('Approve this KYC submission? The user will be notified.')) return;
    setBusy(true);
    try { await approveKYC(kycId); showToast('success', 'KYC submission approved.'); await load(); }
    catch (e) { showToast('error', e.message || 'Failed to approve KYC.'); }
    finally   { setBusy(false); }
  };

  const handleRejectSubmit = async (reason, reasonCode) => {
    setBusy(true);
    try { await rejectKYC(kycId, reason, reasonCode); setRejectOpen(false); showToast('success', 'KYC submission rejected.'); await load(); }
    catch (e) { showToast('error', e.message || 'Failed to reject KYC.'); }
    finally   { setBusy(false); }
  };

  if (loading) return (
    <div className="kycd-page">
      <button type="button" className="kycd-back" onClick={() => navigate('/system-admin/kyc')}><FaArrowLeft /> Back to KYC</button>
      <div className="kycd-loading"><FaSpinner className="kycd-spin kycd-spin--lg" /><p>Loading submission…</p></div>
    </div>
  );

  if (error) return (
    <div className="kycd-page">
      <button type="button" className="kycd-back" onClick={() => navigate('/system-admin/kyc')}><FaArrowLeft /> Back to KYC</button>
      <div className="kyc-alert kyc-alert--error" style={{ marginTop: 24 }}>{error} <button type="button" className="kyc-alert-retry" onClick={load}>Retry</button></div>
    </div>
  );

  const isPending  = kyc.status === 'pending';
  const isRejected = kyc.status === 'rejected';

  return (
    <div className="kycd-page">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <button type="button" className="kycd-back" onClick={() => navigate('/system-admin/kyc')}><FaArrowLeft /> Back to KYC List</button>

      {/* Hero */}
      <div className="kycd-hero">
        <div className="kycd-hero-avatar">{(kyc.name || '?')[0].toUpperCase()}</div>
        <div className="kycd-hero-info">
          <div className="kycd-hero-name">{kyc.name}</div>
          <div className="kycd-hero-email">{kyc.email}</div>
          <div className="kycd-hero-meta">
            <StatusBadge status={kyc.status} />
            <span className="kycd-hero-chip">{kyc.role}</span>
            <span className="kycd-hero-chip">{kyc.idType}</span>
          </div>
        </div>
        {isPending && (
          <div className="kycd-hero-actions">
            <button type="button" className="kycd-btn kycd-btn--approve" onClick={handleApprove} disabled={busy}>
              {busy ? <FaSpinner className="kycd-spin" /> : <FaCheckCircle />} Approve
            </button>
            <button type="button" className="kycd-btn kycd-btn--reject" onClick={() => setRejectOpen(true)} disabled={busy}>
              <FaTimesCircle /> Reject
            </button>
          </div>
        )}
      </div>

      {/* Banners */}
      {kyc.status === 'verified' && kyc.verifiedAt && (
        <div className="kycd-banner kycd-banner--success"><FaCheckCircle /> Approved on {kyc.verifiedAt}</div>
      )}
      {isRejected && kyc.rejectionReason && (
        <div className="kycd-banner kycd-banner--danger">
          <FaTimesCircle />
          <div>
            <strong>Rejected</strong>{kyc.rejectionCode && <span className="kycd-rejection-code"> [{kyc.rejectionCode}]</span>}
            <div className="kycd-rejection-text">{kyc.rejectionReason}</div>
            {kyc.resubmitDeadline && <div className="kycd-rejection-deadline">Resubmit by: {kyc.resubmitDeadline}</div>}
          </div>
        </div>
      )}

      {/* Two columns */}
      <div className="kycd-columns">
        {/* Info column */}
        <div className="kycd-col-info">
          <div className="kycd-card">
            <div className="kycd-card-header"><FaUser /> Personal Information</div>
            <div className="kycd-card-body">
              <InfoRow icon={<FaUser />}       label="Full Name"    value={kyc.name} />
              <InfoRow icon={<FaEnvelope />}   label="Email"        value={kyc.email} />
              <InfoRow icon={<FaPhone />}      label="Phone"        value={kyc.phone} />
              <InfoRow icon={<FaShieldAlt />}  label="Role"         value={kyc.role} />
            </div>
          </div>
          <div className="kycd-card" style={{ marginTop: 16 }}>
            <div className="kycd-card-header"><FaIdCard /> Identity Document</div>
            <div className="kycd-card-body">
              <InfoRow icon={<FaIdCard />}      label="Document Type" value={kyc.idType} />
              <InfoRow icon={<FaIdCard />}      label="ID Number"     value={kyc.idNumber} />
              <InfoRow icon={<FaCalendarAlt />} label="Submitted At"  value={kyc.submittedAt} />
              {kyc.verifiedAt && <InfoRow icon={<FaCalendarAlt />} label="Approved At" value={kyc.verifiedAt} />}
            </div>
          </div>
          {/* Override actions for rejected */}
          {isRejected && (
            <div className="kycd-card" style={{ marginTop: 16 }}>
              <div className="kycd-card-header"><FaShieldAlt /> Actions</div>
              <div className="kycd-card-body">
                <p className="kycd-action-hint">You can override the rejection and approve this submission.</p>
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button type="button" className="kycd-btn kycd-btn--approve" onClick={handleApprove} disabled={busy} style={{ flex: 1 }}>
                    {busy ? <FaSpinner className="kycd-spin" /> : <FaCheckCircle />} Override &amp; Approve
                  </button>
                  <button type="button" className="kycd-btn kycd-btn--reject" onClick={() => setRejectOpen(true)} disabled={busy} style={{ flex: 1 }}>
                    <FaTimesCircle /> Re-Reject
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Docs column */}
        <div className="kycd-col-docs">
          <div className="kycd-card">
            <div className="kycd-card-header"><FaFileAlt /> Submitted Documents</div>
            <div className="kycd-card-body kycd-card-body--docs">
              {!kyc.idFrontUrl && !kyc.idBackUrl && !kyc.selfieUrl ? (
                <div className="kycd-no-docs"><FaFileAlt /><p>No documents were submitted.</p></div>
              ) : (
                <div className="kycd-docs-grid">
                  {kyc.idFrontUrl  && <DocCard label="ID — Front"        originalUrl={kyc.idFrontUrl} />}
                  {kyc.idBackUrl   && <DocCard label="ID — Back"         originalUrl={kyc.idBackUrl} />}
                  {kyc.selfieUrl   && <DocCard label="Selfie / Live Photo" originalUrl={kyc.selfieUrl} />}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {rejectOpen && <RejectModal onClose={() => setRejectOpen(false)} onSubmit={handleRejectSubmit} busy={busy} />}
    </div>
  );
};

export default KYCDetails;
